import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import { getPayloadFromJwtToken } from '../utils';
import { setAuthHeader } from '../axios-utils';

type User = {
  accessToken: string;
  firstName: string;
  lastName: string;
  pnr: string;
};

const initUser = {
  accessToken: '',
  firstName: '',
  lastName: '',
  pnr: '',
};

type UserContextType = {
  currentUser: User;
  logout: () => void;
  onAuthenticated: (user: User) => void;
};

const UserContext = createContext<UserContextType | undefined>(undefined);
UserContext.displayName = 'UserContext';

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('cannot use UserContext outside of UserProvider');
  }
  return context;
};

type UserProviderProps = {
  /** URL to api-auth */
  authBaseUrl: string;
  /** Used to disable getting refresh token in local since it doesn't work */
  disableGetRefreshToken?: boolean;
  children: ReactNode;
};

export const UserProvider = ({ authBaseUrl, disableGetRefreshToken, children }: UserProviderProps) => {
  const [currentUser, setCurrentUser] = useState<User>(initUser);
  const [fetchingNewRefreshToken, setFetchingNewRefreshToken] = useState(true);
  const getNewRefreshTokenBeforeItExpires = () => {
    const runInOneMinute = 1000 * 60;
    setTimeout(() => {
      setFetchingNewRefreshToken(true);
    }, runInOneMinute);
  };

  useEffect(() => {
    if (!fetchingNewRefreshToken) {
      return;
    }

    const gethRefreshToken = async () => {
      try {
        const { accessToken } = await fetchRefreshToken(authBaseUrl);
        const {
          meta: { firstName, lastName, pnr },
        } = getPayloadFromJwtToken(accessToken);
        setCurrentUser({ accessToken, firstName, lastName, pnr });
        setFetchingNewRefreshToken(false);
        getNewRefreshTokenBeforeItExpires();
        setAuthHeader(accessToken);
      } catch {
        sessionStorage.setItem('isLoggedIn', 'false');
        setCurrentUser(initUser);
      }
    };

    if (sessionStorage.getItem('isLoggedIn') === 'true') {
      gethRefreshToken();
    }
  }, [authBaseUrl, fetchingNewRefreshToken]);

  const contextValue = useMemo(() => {
    const onAuthenticated = (currentUser: User) => {
      setCurrentUser(currentUser);
      sessionStorage.setItem('isLoggedIn', 'true');
      // Disabled in local
      if (!disableGetRefreshToken) {
        setFetchingNewRefreshToken(false);
      }
      getNewRefreshTokenBeforeItExpires();
      setAuthHeader(currentUser.accessToken);
    };
    const logout = () => {
      setCurrentUser(initUser);
      sessionStorage.setItem('isLoggedIn', 'false');
      logOut(authBaseUrl);
    };

    return { currentUser, logout, onAuthenticated };
  }, [authBaseUrl, currentUser, disableGetRefreshToken]);

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

const fetchRefreshToken = async (authBaseUrl: string) => {
  const response = await axios.get(`${authBaseUrl}/public/token?grant_type=refresh_token&authDomain=VERIFIED_USER`, {
    withCredentials: true,
  });

  return response.data;
};

const logOut = async (authBaseUrl: string) => {
  axios.get(`${authBaseUrl}/public/logout?authDomain=VERIFIED_USER`);
};
